<template>
  <div class="course__list">
    <empty-table v-if="!courses.length"/>

    <v-data-table
        v-else
        :items="courses"
        :headers="headers"
        item-key="uniqueCourseId"
        tile
        :hide-default-footer="true"
        :disable-pagination="true"
        :custom-sort="customSort"
        @update:sort-by="goTop"
        @update:sort-desc="goTop"
        :fixed-header="true"
        :height="courses.length > 10 ? '65vh' : null"
        :no-data-text="$t('course.noData')"
    >
      <template v-slot:[`header.shortName`]>
      </template>

      <!-- msg, resource, upcomingEventsCount, other -->
      <template v-for="value in headerIconList" v-slot:[`header.${value}`]="{header}" >
        <v-icon color="black" :key="value">mdi-{{header.icon}}</v-icon>
      </template>

      <template v-slot:[`item.shortName`]="{item}">
        <div class="course__name">
          <div class="name__display">
            <favorite-button :course="favoriteInfo(item)"/>
            <a
              :href="item.url"
              :target="linksInNewTab ? '_blank' : '_self'"
            >
              {{ item.shortName }}
            </a>
          </div>
          <analytics-button
              :has-analytics="item.hasAnalytics"
              :course-id="item.courseId"
          />
        </div>
      </template>

      <template v-slot:[`item.msg`]="{ item }">
        {{ item.msg||''}}
      </template>

      <template v-slot:[`item.resource`]="{ item }">
        {{ item.resource||''}}
      </template>

      <template v-slot:[`item.upcomingEventsCount`]="{ item }">
        <a
          :href="calendarUrl(item.url, item.courseId)"
          :target="linksInNewTab ? '_blank' : '_self'"
          v-if="item.upcomingEventsCount !== 0"
          :aria-label="ariaCalendar(item)"
        >
          {{ item.upcomingEventsCount}}
        </a>
        <span v-else></span>
      </template>

      <template v-slot:[`item.other`]="{ item }">
        {{ item.other||''}}
      </template>

      <template v-slot:[`item.courseGrade`]="{ item}">
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <p
              v-if="item.gradeRaw !== null"
              v-on="on"
              v-html="displayGrade(item.courseGrade)"
              v-bind="attrs"
            ></p>
          </template>
          <span>{{ item.courseGrade.finalGrade }}</span>
        </v-tooltip>
      </template>

      <template v-slot:[`item.completion`]="{ item }">
        <v-progress-circular
            v-if="item.completion.percentage !== null"
            :value="Math.floor(item.completion.percentage)"
            rotate="-90"
            size="54"
            width="7"
            :color="userColor"
        >
          <span v-html="displayCompletion(item.completion.percentage)">
          </span>
        </v-progress-circular>
      </template>

    </v-data-table>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex'
  import { getCalendarUrl, USER_COLOR_CLASS_MAP } from '@/assets/constants'
  import ResponsiveMixin from '@/components/common/ResponsiveMixin'
  import pick from 'lodash/fp/pick'

    export default {
      name: "MoodleList",
      mixins: [ResponsiveMixin],
      components: {
        EmptyTable: () => import(/*webpackChunkName: "moodle" */ '@/components/common/EmptyTable'),
        AnalyticsButton: () => import(/*webpackChunkName: "course" */ '@/components/course/AnalyticsButton'),
        FavoriteButton: () => import(/*webpackChunkName: "course" */ '@/components/course/FavoriteButton'),
      },
      props: {
        name: {type:String},
        courses: {type:Array, required:true}
      },
      data() {
        return {
          headerIconList: ['msg', 'resource', 'upcomingEventsCount', 'other']
        }
      },
      computed:{
        ...mapGetters(['linksInNewTab', 'userColor']),
        headers() {
          const MobileWidth = { width: '8.5%' }
          return [
            {
              text: this.$t('course.listHeader.shortName'),
              value: 'shortName',
              // width: '36%'
            },
            {
              text: this.$t('course.listHeader.msg'),
              value: 'msg',
              align: 'center',
              icon: 'email',
              ...this.notSmallScreen ? {} : MobileWidth
            },
            {
              text: this.$t('course.listHeader.resource'),
              value: 'resource',
              align: 'center',
              icon: 'file-document-outline',
              ...this.notSmallScreen ? {} : MobileWidth
            },
            {
              text: this.$t('course.listHeader.event'),
              value: 'upcomingEventsCount',
              align: 'center',
              icon: 'calendar-month-outline',
              ...this.notSmallScreen ? {} : MobileWidth
            },
            {
              text: this.$t('course.listHeader.other'),
              value: 'other',
              align: 'center',
              icon: 'school',
              ...this.notSmallScreen ? {} : MobileWidth
            },
            {
              text: this.$t('course.listHeader.courseGrade'),
              value: 'courseGrade',
              align: 'center',
              ...this.notSmallScreen ? {} : { width: '9%' }
            },
            {
              text: this.$t('course.listHeader.completion'),
              value: 'completion',
              align: 'center',
              ...this.notSmallScreen ? {} : { width: '18%' }
            }
          ].map(header => ({ ...header, class: USER_COLOR_CLASS_MAP[this.userColor]}))
        }
      },
      methods: {
        ariaCalendar(course) {
          const count = this.$tc(
                'course.ariaLabel.event', course.upcomingEventsCount
              )
          const description = this.$t(
                  'course.ariaLabel.calendar', {
                    courseName: course.shortName,
                    instanceName: this.name
                  })
          return `${count}${description}`

        },
        customSort: function(items, groupBy, isDesc) {
          items.sort((a, b) => {
            const DescFactor = isDesc[0] ? -1 : 1
            if (groupBy[0]==='completion') {
              const aPercent = a.completion.percentage
              const bPercent = b.completion.percentage
              if(aPercent === null) {
                if(bPercent === null)
                  return 0;
                else
                  return 1;
              }
              if(bPercent === null)
                return -1;
              return DescFactor * Math.sign(aPercent - bPercent)

            } else if (groupBy[0]==='shortName') {
              return DescFactor * a.shortName.localeCompare(b.shortName)
            } else if (groupBy[0]==='courseGrade') {
              const { finalGrade: aGrade, maxGrade: aMax} = a.courseGrade
              const { finalGrade: bGrade, maxGrade: bMax} = b.courseGrade
              if(aGrade === null) {
                if(bGrade === null)
                  return 0
                else
                  return 1
              }
              if(bGrade === null)
                return -1
              return DescFactor * Math.sign(aGrade/aMax - bGrade/bMax)
            }
            else {
              if (groupBy[0] !== undefined) {
                return a[groupBy[0]] === b[groupBy[0]]
                    ? 0
                    : DescFactor * (a[groupBy[0]]<b[groupBy[0]]
                        ? -1
                        : 1
                    )
              }
              return 0
            }
          })
          return items;
        },
        /**
         * @param {Number} percentage - completion percentage
         * @returns {String}
         */
        displayCompletion: (percentage) => {
          let completion = ''
          if (percentage !== null) {
            if (percentage === 100) {
              completion = '<i class="v-icon notranslate mdi mdi-check-bold theme--light green--text"></i>'
            } else {
              completion = Math.floor(percentage)
            }
          }
          return completion
        },
        /**
         * @param {CourseGrade} courseGrade
         * @returns {String}
         */
        displayGrade: function({finalGrade, maxGrade}) {
          if (finalGrade !== null){
            const roundedNote = Math.round(finalGrade * 10) / 10
            return `<span class="grade__display">${roundedNote}</span>/${maxGrade}`
          }
          return ''
        },
        favoriteInfo(course){
          return pick(
              ['isFavorite', 'shortName', 'courseId', 'instanceId'],
              course
          )
        },
        goTop () {
          const tableWrapper = this.$el.getElementsByClassName('v-data-table__wrapper')[0]
          tableWrapper.scrollTop = 0
        },
        /**
         * Clean last empty cells on mobile, up to two
         */
        removeEmptyLastCells: function () {
          if (this.courses.length) {
            const courseRows = this.$el.querySelector('tbody').children
            for (let i=0; i<courseRows.length ; i++) {
              const trElement = courseRows.item(i)
              const completionCell = trElement.cells[6]
              if (!completionCell.lastElementChild.textContent.trim()) {
                completionCell.style.display = "none"
                const gradeCell = trElement.cells[5]
                if (!gradeCell.lastElementChild.textContent.trim()) {
                  gradeCell.style.display = "none"
                  gradeCell.previousElementSibling.style.borderBottom = 'thin solid rgba(0, 0, 0, 0.12)'
                } else {
                  gradeCell.style.borderBottom = 'thin solid rgba(0, 0, 0, 0.12)'
                }
              }
            }
          }
        },
        /**
         * remove custom mobile style from three last cells of a raw
         */
        restoreEmptyCells: function () {
          if (this.courses.length) {
            const courseRows = this.$el.querySelector('tbody').children
            for (let i=0; i<courseRows.length ; i++) {
              for (let index = 4; index <= 6; index++) {
                courseRows.item(i).cells[index].removeAttribute('style')
              }
            }
          }
        },
        calendarUrl: getCalendarUrl,
      },
      watch: {
        isMobile(newState) {
          this.$nextTick(newState
              ? this.removeEmptyLastCells
              : this.restoreEmptyCells)
        }
      },
      mounted() {
        if (this.isMobile) {
          this.removeEmptyLastCells()
        }
      }
    }
</script>

<style lang="scss" scoped>
@import 'src/scss/constants';

.v-data-table::v-deep {
  th {
    font-size: 15px;
    font-weight: 700;
    white-space: nowrap;
  }
  td {
    .course__name{
      display: flex;
      justify-content: space-between;
      padding-right: 10px;
    }
    font-size: 18px;
    font-weight: 500;
    p{
      margin: 0;
    }
    a{
      font-size: 17px;
      font-weight: 700;
    }
    .v-progress-circular__info{
      color: $vuetifyDark;
    }

    @media screen and (max-width: $mediaXSmall){
      &:nth-of-type(2){
        div:first-of-type{
          font-size: 0;
          &:after {
            font-size: large;
            content: "\f0e0";
            font-family: FontAwesome, serif;
          }
        }
      }
      &:nth-of-type(3){
        div:first-of-type {
          font-size: 0;
          &:after {
            font-size: large;
            content: "\f15c";
            font-family: FontAwesome, serif;
          }
        }
      }
      &:nth-of-type(4){
        div:first-of-type {
          font-size: 0;
          &:after {
            font-size: large;
            content: "\f073";
            font-family: FontAwesome, serif;
          }
        }
      }
      &:nth-of-type(5){
        div:first-of-type {
          font-size: 0;
          &:after {
            font-size: large;
            content: "\f19d";
            font-family: FontAwesome, serif;
          }
        }
      }
      &:first-of-type {
        justify-content: center;
        div{
          text-align: center;
          &.v-data-table__mobile-row__header{
            display: none;
          }
        }
      }
    }

    @media screen and (max-width: $mediaSmall) {
      a{
        font-size: 15px;
      }
    }
  }
}
</style>
